import { useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import { theme } from '@/styles';
import textsData from '@/textData/DashboardTexts.json';
import { useUserLanguage, useErrorHandlerContext } from '@/customHooks';
import 'react-calendar/dist/Calendar.css';
import '@/styles/custom-calendar.css';
import { useAllContractsForDashboard } from '@/customHooks/useAllContractsForDashboard';
import { useDashboardFilter } from '@/contexts/DashboardFilterContext';
import { DashboardCardPeriodCaption } from '../atoms';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

export type CalendarEventColor = 'purple' | 'green' | 'yellow' | 'red';

export type CalendarEvent = {
  id: string;
  title: string;
  date: Date;
  color: CalendarEventColor;
  type: 'contract';
  entityId: string;
  variant: 'start' | 'end';
};

const EVENT_COLORS: Record<CalendarEventColor, string> = {
  purple: theme.colors.secondary1,
  green: theme.colors.primary1,
  yellow: '#E8C547',
  red: theme.colors.error,
};

const contractsRoadmapPath = `/${pathURL.USER}/${pathURL.REPORTS}/${pathURL.CONTRACTS_ROADMAP}`;

export const DashboardCalendar = () => {
  const [value, onChange] = useState<Value>(new Date());
  const [showDetail, setShowDetail] = useState(true);
  const userLanguage = useUserLanguage();
  const { open: errorHandle } = useErrorHandlerContext();
  const navigate = useNavigate();

  const { dashboardFilter } = useDashboardFilter();
  const { contracts, error } = useAllContractsForDashboard({ dashboardFilter });

  if (error) errorHandle(error);

  const events: CalendarEvent[] = useMemo(() => {
    if (!contracts?.length) return [];

    const startTitle =
      userLanguage === 'es' ? 'Inicio de contrato' : 'Contract start';
    const endTitle =
      userLanguage === 'es' ? 'Fin de contrato' : 'Contract end';

    const result: CalendarEvent[] = [];

    contracts.forEach((contract) => {
      if (contract.startDate) {
        const d = new Date(contract.startDate as string);
        if (!Number.isNaN(d.getTime())) {
          result.push({
            id: `${contract.id}-start`,
            title: startTitle,
            date: d,
            color: 'green',
            type: 'contract',
            entityId: contract.id,
            variant: 'start',
          });
        }
      }
      if (contract.finishDate) {
        const d = new Date(contract.finishDate as string);
        if (!Number.isNaN(d.getTime())) {
          result.push({
            id: `${contract.id}-end`,
            title: endTitle,
            date: d,
            color: 'red',
            type: 'contract',
            entityId: contract.id,
            variant: 'end',
          });
        }
      }
    });

    return result;
  }, [contracts, userLanguage]);

  const texts =
    userLanguage === 'es' ? textsData.es.calendarCard : textsData.en.calendarCard;

  const selectedDate = Array.isArray(value) ? value[0] : value;

  const getEventsForDate = (date: Date) => {
    return events.filter((e) => e.date.toDateString() === date.toDateString());
  };

  const handleTileClick = (date: Date) => {
    onChange(date);
    setShowDetail(true);
  };

  const navigationLabel = ({ date }: { date: Date }) => {
    const locale = userLanguage === 'es' ? 'es' : 'en-US';
    const monthRaw = date.toLocaleDateString(locale, { month: 'long' });
    const month = monthRaw.charAt(0).toUpperCase() + monthRaw.slice(1);
    const year = date.getFullYear();
    return (
      <>
        <strong>{month}</strong>
        <span className="calendar-year">{year}</span>
      </>
    );
  };

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  const handleEventClick = (event: CalendarEvent) => {
    switch (event.type) {
      case 'contract': {
        navigate(`/${pathURL.USER}/${pathURL.CONTRACT}/${pathURL.TENANT}/${pathURL.DETAIL}`, {
          state: { id: event.entityId },
        });
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className="rounded-xl bg-white p-6 shadow-[0_14px_32px_rgba(15,23,42,0.16)]">
      <div className="mb-5 flex items-center justify-between">
        <div className="flex min-w-0 flex-col items-start">
          <h3 className="m-0 text-[20px] font-bold text-[#1d2f35]">{texts.title}</h3>
          <DashboardCardPeriodCaption tightTop />
        </div>
        <button
          className="border-none bg-transparent p-0 font-[Montserrat,Arial,sans-serif] text-sm font-bold text-[#4A9462] hover:underline"
          type="button"
          onClick={() => navigate(contractsRoadmapPath)}
        >
          {texts.seeMore}
        </button>
      </div>
      <hr className="mb-4 mt-[0.4rem] border-0 border-t border-t-[#E5E7EB]" />

      <Calendar
        onChange={onChange}
        onClickDay={handleTileClick}
        value={value}
        locale={userLanguage === 'es' ? 'es' : 'en-US'}
        formatShortWeekday={(_, date) =>
          date
            .toLocaleDateString(userLanguage === 'es' ? 'es' : 'en-US', {
              weekday: 'short',
            })
            .slice(0, 3)
        }
        navigationLabel={navigationLabel}
        prev2Label={null}
        next2Label={null}
        prevLabel={<span aria-hidden>‹</span>}
        nextLabel={<span aria-hidden>›</span>}
        tileClassName={({ date, view }) => {
          if (view !== 'month') return '';
          const classes: string[] = [];
          if (selectedDate && date.toDateString() === selectedDate.toDateString()) {
            classes.push('calendar-selected-day');
          }
          if (date.toDateString() === new Date().toDateString()) {
            classes.push('calendar-today');
          }
          return classes.join(' ');
        }}
        tileContent={({ date }) => {
          const dayEvents = getEventsForDate(date);
          if (dayEvents.length === 0) return null;
          return (
            <div className="mt-1 flex flex-wrap justify-center gap-1">
              {dayEvents.slice(0, 3).map((e, i) => (
                <span key={e.id + i} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: EVENT_COLORS[e.color] }} />
              ))}
            </div>
          );
        }}
      />

      {showDetail && selectedDate && (
        <div className="mt-4 border-t border-t-[#E5E7EB] pt-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="font-bold text-[#1F2937]">
              {selectedDate.toLocaleDateString(
                userLanguage === 'es' ? 'es' : 'en-US',
                { month: 'long', day: 'numeric', year: 'numeric' }
              )}
            </span>
            <button
              className="flex h-6 w-6 items-center justify-center rounded border-none bg-transparent text-[1.25rem] leading-none text-gray-500 hover:bg-[#F8FAFC] hover:text-[#1F2937]"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowDetail(false);
              }}
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>

          <div className="mb-3 flex flex-wrap gap-2">
            {selectedDateEvents.map((event) => (
              <div
                key={event.id}
                className="flex cursor-pointer items-center gap-2 rounded-lg bg-[#F8FAFC] px-3 py-2 transition duration-150 ease-in-out active:scale-[0.98] hover:bg-[#EEF2F7]"
                onClick={() => handleEventClick(event)}
              >
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: EVENT_COLORS[event.color] }} />
                <span className="text-sm text-[#1F2937]">{event.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
